import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getCurrentAircraftId } from '../aircraft/aircraftStore';

const API_BASE =
  process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'http://localhost:8000';

export const AIRCRAFT_SCOPED_PREFIXES = [
  '/flights',
  '/forms',
  '/bookings',
  '/issues',
  '/modernizations',
  '/finance',
  '/aircraft',
  '/stats',
];

export type AuthUser = {
  id: number;
  name: string;
  login: string;
  role: string;
  color?: string | null;
  date_format?: 'dot' | 'long';
  hide_colors?: boolean;
  club_id?: number | null;
  club_role?: string | null;
  club_name?: string | null;
  is_pilot?: boolean;
  is_support?: boolean;
};

export type SessionResponse = AuthUser & {
  access_token: string;
  token_type?: string;
  expires_in?: number;
  /** Только при X-Client: native|android|ios|mobile — хранить в secure storage */
  refresh_token?: string;
};

/** Access JWT в memory (не localStorage). Cookies — HttpOnly на API. */
let accessToken: string | null = null;
let refreshPromise: Promise<SessionResponse> | null = null;

export const setAccessToken = (token: string | null) => {
  accessToken = token;
};

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

/** Без response-interceptor — чтобы /refresh не зацикливался */
export const bareApi = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  const url = config.url || '';
  const scoped = AIRCRAFT_SCOPED_PREFIXES.some(
    (prefix) =>
      url === prefix ||
      url.startsWith(`${prefix}/`) ||
      url.startsWith(`${prefix}?`)
  );
  const aid = getCurrentAircraftId();
  if (scoped && aid > 0) {
    config.params = {
      aircraft_id: aid,
      ...(config.params || {}),
    };
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined;
    if (!original || error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    const url = original.url || '';
    if (
      url.includes('/users/login') ||
      url.includes('/users/refresh') ||
      url.includes('/users/logout')
    ) {
      return Promise.reject(error);
    }

    original._retry = true;
    try {
      if (!refreshPromise) {
        refreshPromise = bareApi
          .post<SessionResponse>('/users/refresh')
          .then((res) => {
            accessToken = res.data.access_token;
            return res.data;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }
      await refreshPromise;
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${accessToken}`;
      return api(original);
    } catch (refreshError) {
      accessToken = null;
      const path = window.location.pathname;
      const isPublicAuth =
        path === '/login' ||
        path.startsWith('/register') ||
        path.startsWith('/invite');
      // Не срываем /register и /invite жёстким редиректом на логин.
      if (!isPublicAuth) {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    }
  }
);
